import { AuthContext, useIsHydrated } from '@core/shared';
import { BlacklistSource, OptionsApi } from '@core/swagger-client';
import { Button, Close, Confirm, ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, Input, Loader, Search } from '@core/ui';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { getI18n, Trans, useTranslation } from 'react-i18next';

const formatDate = (value?: string) => (value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-');

export const ManageBlacklistPage = () => {
    const canManageBlacklist = useContext(AuthContext).canManageBlacklist;
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(handle);
    }, [search]);

    const searchQuery = OptionsApi.blacklist.search(debouncedSearch || undefined);
    const { data: entries, isLoading, error } = useQuery(searchQuery);

    const removeMutation = useMutation(OptionsApi.blacklist.remove());
    const [confirmEmail, setConfirmEmail] = useState<string | null>(null);

    if (!canManageBlacklist) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const handleRemove = () => {
        if (!confirmEmail) return;
        removeMutation.mutate(confirmEmail, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: searchQuery.queryKey });
            }
        });
        setConfirmEmail(null);
    };

    return (
        <ContainerDefault>
            <BackLink to="/manage">{t('Button.BackToManageEnv')}</BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('Blacklist.PageHeading')}
            </Heading>

            {removeMutation.error && <ErrorMessage apiError={removeMutation.error} classNames="mb-4 max-w-4xl" />}

            <div className="max-w-lg mb-8">
                <Input
                    data-testid="blacklist-search"
                    label={t('Blacklist.Search.Label')}
                    placeholder={t('Blacklist.Search.Placeholder')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                >
                    <Search classNames="size-6 flex-none self-center -order-1 mr-4 opacity-75 stroke-current" />
                    {search.length > 0 && (
                        <Button iconbtn aria-label={t('Search.Button.Clear.AriaLabel')} onClick={() => setSearch('')}>
                            <Close classNames="size-6 stroke-current" />
                        </Button>
                    )}
                </Input>
            </div>

            {error ? (
                <ErrorMessage apiError={error} classNames="mb-4 max-w-4xl" />
            ) : isLoading ? (
                <Loader />
            ) : entries && entries.length > 0 ? (
                <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                    <table className="table table-striped mt-2" data-testid="blacklist-table">
                        <thead>
                            <tr>
                                <th>{t('Blacklist.Table.Header.Email')}</th>
                                <th>{t('Blacklist.Table.Header.BlacklistedAt')}</th>
                                <th>{t('Blacklist.Table.Header.Source')}</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => (
                                <tr key={entry.email}>
                                    <td>{entry.email}</td>
                                    <td>{formatDate(entry.blacklistedAt)}</td>
                                    <td>{t(`Blacklist.Source.${BlacklistSource[entry.source]}`)}</td>
                                    <td>
                                        <Button
                                            data-testid={`blacklist-remove-${entry.email}`}
                                            onClick={() => setConfirmEmail(entry.email)}
                                            iconbtn
                                            classNames="-my-4 translate-y-1.25"
                                            disabled={!isHydrated}
                                            isLoading={removeMutation.isPending}
                                        >
                                            <Close classNames="size-6 stroke-warning" />
                                            <span className="sr-only">
                                                <Trans values={{ email: entry.email }} i18nKey="Blacklist.Button.Remove" />
                                            </span>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>{t('Blacklist.Empty')}</p>
            )}

            <Confirm
                open={confirmEmail !== null}
                onClose={() => setConfirmEmail(null)}
                onConfirm={handleRemove}
                title={t('Blacklist.Remove.Title')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('Blacklist.Remove.Confirm')}
                confirmTestId="blacklist-remove-confirm"
            >
                <p>
                    <Trans values={{ email: confirmEmail ?? '' }} i18nKey="Blacklist.Remove.Body" />
                </p>
            </Confirm>
        </ContainerDefault>
    );
};
